import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate } from 'react-router';

import { requestStatus } from '../constants/appConstants';
import { requestKeys } from '../queries/query-keys';
import { useGetRequestListQuery, useRegisterRequestMutation } from '../queries/requestListQuery';
import { selectedTeamIdQuery } from '../queries/teamQuery';
import { queryClient } from '../router/Root';
import { useZustand } from '../zustandStore';

export const useCreateRequest = () => {
	const navigate = useNavigate();
	const { setPageLoading } = useZustand();
	const [isNewRequestLoading, setIsNewRequestLoading] = useState(false);

	const { data: teamId } = useQuery(selectedTeamIdQuery('selectedTeamID'));
	const { data: requests, isFetching: isGetRequestsFetching } = useGetRequestListQuery();
	const registerRequest = useRegisterRequestMutation();

	const createRequest = async (e?: ReactMouseEvent<Element>) => {
		e?.preventDefault();
		setPageLoading(true);
		if (isNewRequestLoading || isGetRequestsFetching) {
			setPageLoading(false);
			return;
		}
		setIsNewRequestLoading(true);

		const createdRequest = Array.isArray(requests)
			? requests.find(request => {
					if (teamId?.[0]) {
						return request.state === requestStatus.CREATED && request.teamId === teamId?.[0];
					}
					return request.state === requestStatus.CREATED;
				})
			: { requestId: '' };

		if (!_.isEmpty(createdRequest) && createdRequest?.requestId) {
			void navigate(`/requests/${createdRequest.requestId}`);
			setIsNewRequestLoading(false);
			setPageLoading(false);
		} else {
			const requestId = crypto.randomUUID();
			registerRequest.mutate(
				{ requestId },
				{
					onSuccess: () => {
						void navigate(`/requests/${requestId}`);
					},
					onSettled: async () => {
						setPageLoading(false);
						setIsNewRequestLoading(false);
						await queryClient.invalidateQueries({
							queryKey: requestKeys.getRequestList(),
						});
					},
				},
			);
		}
	};

	return { createRequest, isLoading: isNewRequestLoading || isGetRequestsFetching };
};
